import { auth } from "@/main";
import { RoleEnum, RoutesEnum, SubscriptionCopy } from "@/enums";
import { onAuthStateChanged, verifyPasswordResetCode, applyActionCode, getIdTokenResult } from "firebase/auth";
import service from "@/services";
import { useUserStore } from '@/store/store'
import { pinia } from '@/plugins/pinia'

const userStore = useUserStore(pinia)

export const authenticated = (to, from, next) => {
  onAuthStateChanged(auth, async (user) => {
    if (user) {
      next();
    } else {
      next(RoutesEnum.LOGIN);
    }
  });
};

export const notAuthenticated = (to, from, next) => {
  onAuthStateChanged(auth, async (user) => {
    if (!user) {
      return next();
    } else {
      return next(RoutesEnum.VERIFY_EMAIL)
    }
  });
};

export const verified = (to, from, next) => {
  onAuthStateChanged(auth, async (user) => {
    if (user?.emailVerified) {
      let user = userStore.get
      if (!user.id)
        user = await service.user.get();
      userStore.set(user);
      to.meta.user = user;
      next();
    } else {
      next(RoutesEnum.VERIFY_EMAIL);
    }
  });
};

export const notVerified = (to, from, next) => {
  onAuthStateChanged(auth, async (user) => {
    const { oobCode } = to.query;
    if (!user?.emailVerified) {
      if (!oobCode) {
        return next();
      } else {
        try {
          await applyActionCode(auth, oobCode);
          await auth.currentUser.reload();
          return next(await service.progress.getCurrentRoute());
        } catch (error) {
          console.log(error)
          return next({ path: RoutesEnum.EXPIRED_VERIFICATION, query: { oobCode }, replace: true })
        }
      }
    } else {
      let user = userStore.get
      if (!user.id)
        user = await service.user.get();
      userStore.set(user);
      to.meta.user = user;
      return next(await service.progress.getCurrentRoute());
    }
  });
};

export const oobCode = (to, from, next) => {
  onAuthStateChanged(auth, async () => {
    if (to.query.oobCode) {
      next();
    } else {
      next(RoutesEnum.VERIFY_EMAIL)
    }
  });
};

export const isAdmin = (to, from, next) => {
  onAuthStateChanged(auth, async (user) => {
    if (!user) return; // keep behavior: only proceed if admin
    const tokenResult = await getIdTokenResult(user, true); // refresh to get latest claims
    const role = tokenResult?.claims?.role;
    if (role === RoleEnum.BETA_ADMINISTRATOR) next();
  });
};

export const isRepresentative = (to, from, next) => {
  onAuthStateChanged(auth, async () => {
    const user = to.meta.user;
    const role = user.role?.name;
    const isInternalNav = Boolean(from && from.name);

    if (role === RoleEnum.REPRESENTATIVE || role === RoleEnum.BETA_ADMINISTRATOR) {
      return next();
    } else {
      if (isInternalNav) {
        return next(false);
      } else {
        const fallback = await service.progress.getCurrentRoute();
        return next(fallback);
      }
    }
  });
};

export const isOwner = (to, from, next) => {
  onAuthStateChanged(auth, async () => {
    const user = to.meta.user;
    if (user.role?.name === RoleEnum.OWNER) {
      next()
    } else {
      next(from.path)
    }
  });
};

export const onboarded = (to, from, next) => {
  onAuthStateChanged(auth, async () => {
    const progresses = await service.progress.get();
    progresses.pop()
    const progress = progresses?.find((progress) => !progress.completed)
    if (!progress) {
      next();
    } else {
      next(progress?.route);
    }
  });
};

export const notOnboarded = (to, from, next) => {
  onAuthStateChanged(auth, async () => {
    const progresses = await service.progress.get();
    const lastRoute = progresses.pop()
    const progress = progresses?.find((progress) => !progress.completed)
    if (progress) {
      next();
    } else {
      next(lastRoute?.route);
    }
  });
};

export const hasAccessCode = (to, from, next) => {
  onAuthStateChanged(auth, async () => {
    try {
      const user = await service.user.get()
      const canAccess = user.role?.name !== RoleEnum.REPRESENTATIVE || user.organization_id
      if(canAccess) {
        next();
      } else {
        next(RoutesEnum.ACCESS_CODE)
      }
    } catch (error) {
      console.log(error);
    }
  });
}

export const notAccessCode = (to, from, next) => {
  onAuthStateChanged(auth, async () => {
    try {
      const user = await service.user.get()
      const canAccess = user.role?.name !== RoleEnum.REPRESENTATIVE || user.organization_id
      if(!canAccess) {
        next();
      } else {
        next(await service.progress.getCurrentRoute())
      }
    } catch (error) {
      console.log(error);
    }
  });
}

export const notResetPasswordCodeVerified = (to, from, next) => {
  const { oobCode } = to.query;
  verifyPasswordResetCode(auth, oobCode)
    .then((verifiedEmail) => {
      next({
        path: RoutesEnum.RESET_PASSWORD,
        query: { email: verifiedEmail, oobCode },
        replace: true,
      });
    })
    .catch(() => {
      next();
    });
};

export const hasResetPasswordCodeVerified = (to, from, next) => {
  const { oobCode } = to.query;
  verifyPasswordResetCode(auth, oobCode)
    .then((verifiedEmail) => {
      if (!to.query.email) {
        return next({
          path: to.path,
          query: { ...to.query, email: verifiedEmail },
          replace: true,
        });
      }
      next();
    })
    .catch(() => {
      next({
        path: RoutesEnum.EXPIRED_RESET_PASSWORD_LINK,
        query: { oobCode },
        replace: true,
      });
    });
};

export const authorizeBusinessAccess = (to, from, next) => {
  onAuthStateChanged(auth, async () => {
    const user = await service.user.get();
    const businessId = Number(to.params.id);

    if (user.role?.name !== RoleEnum.OWNER) {
      return next();
    }

    if (!user.business_id) {
      return next(RoutesEnum.BUSINESS_DASHBOARD);
    }

    if (businessId !== user.business_id) {
      return next(RoutesEnum.BUSINESS_DASHBOARD);
    }

    return next();
  });
};

export const hasActiveSubscription = (to, from, next) => {
  onAuthStateChanged(auth, async () => {
    try {
      const user = await service.user.get();
      const role = String(user?.role?.name || "").trim();

      const isOwner =
        role === RoleEnum.OWNER ||
        role === RoleEnum.BUSINESS_ADMIN;

      if (!isOwner) {
        return next();
      }

      const summary = await service.billing.getSubscriptionSummary();
      const state = summary?.state;

      if (
        state === SubscriptionCopy.STATE.ACTIVE ||
        state === SubscriptionCopy.STATE.TRIAL ||
        state === SubscriptionCopy.STATE.CANCELING
      ) {
        return next();
      }

      return next(RoutesEnum.SETTINGS);
    } catch (err) {
      console.error("[hasActiveSubscription]", err);
      return next();
    }
  });
};

export const notActiveSubscription = (to, from, next) => {
  onAuthStateChanged(auth, async () => {
    try {
      const user = await service.user.get();
      const role = String(user?.role?.name || "").trim();

      const isOwner =
        role === RoleEnum.OWNER ||
        role === RoleEnum.BUSINESS_ADMIN;

      if (!isOwner) {
        return next();
      }

      const summary = await service.billing.getSubscriptionSummary();
      const state = summary?.state;

      if (
        state === SubscriptionCopy.STATE.ACTIVE ||
        state === SubscriptionCopy.STATE.TRIAL ||
        state === SubscriptionCopy.STATE.CANCELING
      ) {
        return next(RoutesEnum.SETTINGS);
      }

      return next();
    } catch (err) {
      console.error("[notActiveSubscription]", err);
      return next();
    }
  })
};
